import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as L from 'leaflet';
import { TranslateModule } from '@ngx-translate/core';

type PlaceSource = 'nominatim' | 'custom';
type TaskStatus = 'open' | 'in-progress' | 'done';

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type?: string;
}

interface Place {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  source: PlaceSource;
}

interface Task {
  id: string;
  placeId: string;
  note: string;
  dueDate?: string;
  status: TaskStatus;
}

@Component({
  selector: 'app-id-tasks',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './id-tasks.component.html',
  styleUrls: ['./id-tasks.component.scss']
})
export class IDTasksComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer') mapContainer!: ElementRef<HTMLDivElement>;

  map?: L.Map;
  placeMarkers: Record<string, L.Marker> = {};
  taskMarkerLayer?: L.LayerGroup;
  searchPlaceIds: Set<string> = new Set<string>();

  searchQuery = '';
  searchResults: Place[] = [];
  places: Place[] = [];
  selectedPlace?: Place;
  tasks: Task[] = [];
  modeShowTasksOnMap = true;

  newCustomName = '';
  newCustomAddress = '';
  newTaskNote = '';
  newTaskDueDate = '';
  newTaskStatus: TaskStatus = 'open';

  readonly statuses: TaskStatus[] = ['open', 'in-progress', 'done'];

  ngAfterViewInit() {
    this.map = L.map(this.mapContainer.nativeElement, {
      center: [52.2297, 21.0122],
      zoom: 14
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(this.map);

    this.taskMarkerLayer = L.layerGroup().addTo(this.map);
    this.map.on('click', (e: L.LeafletMouseEvent) => {
      this.addCustomPlaceAtLocation(e.latlng.lat, e.latlng.lng);
    });
  }

  ngOnDestroy() {
    this.map?.off();
    this.map?.remove();
  }

  async searchPlaces() {
    if (!this.searchQuery.trim()) {
      this.searchResults = [];
      return;
    }

    const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=20&q=${encodeURIComponent(this.searchQuery.trim())}`;
    try {
      const results = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      const data = await results.json();

      // Czyścimy poprzednie wyniki wyszukiwania i markery
      this.searchResults.forEach(result => {
        if (this.placeMarkers[result.id]) {
          this.map?.removeLayer(this.placeMarkers[result.id]);
          delete this.placeMarkers[result.id];
          this.searchPlaceIds.delete(result.id);
        }
      });

      this.searchResults = (data || []).map((item: NominatimResult, idx: number) => ({
        id: `nominatim-${idx}-${item.place_id}`,
        name: item.display_name || 'Znalezisko',
        address: item.display_name || '',
        lat: Number(item.lat),
        lng: Number(item.lon),
        source: 'nominatim' as PlaceSource
      }));

      this.searchResults.forEach((place) => {
        this.addPlaceMarker(place);
        this.searchPlaceIds.add(place.id);
      });

      if (this.searchResults.length > 0) {
        this.flyToLocation(this.searchResults[0]);
      }
    } catch (error) {
      console.error('Błąd wyszukiwania miejsc', error);
    }
  }

  selectPlace(place: Place) {
    this.selectedPlace = place;
    this.flyToLocation(place);
    this.refreshMarkerHighlights();
  }

  addCustomPlace() {
    const name = this.newCustomName.trim() || 'Nowy punkt';
    const address = this.newCustomAddress.trim() || 'Brak adresu';

    if (!this.map) {
      return;
    }

    const center = this.map.getCenter();
    const place: Place = {
      id: `custom-${Date.now()}`,
      name,
      address,
      lat: center.lat,
      lng: center.lng,
      source: 'custom'
    };

    this.places.push(place);
    this.addPlaceMarker(place);
    this.selectPlace(place);

    this.newCustomName = '';
    this.newCustomAddress = '';
  }

  addCustomPlaceAtLocation(lat: number, lng: number) {
    const defaultName = `Własny punkt ${this.places.filter(p => p.source === 'custom').length + 1}`;
    const place: Place = {
      id: `custom-${Date.now()}`,
      name: this.newCustomName.trim() || defaultName,
      address: this.newCustomAddress.trim() || 'Kliknięto na mapie',
      lat,
      lng,
      source: 'custom'
    };

    this.places.push(place);
    this.addPlaceMarker(place);
    this.selectPlace(place);
  }

  addPlaceMarker(place: Place) {
    if (!this.map) {
      return;
    }

    const existing = this.placeMarkers[place.id];
    if (existing) {
      existing.setLatLng([place.lat, place.lng]);
      return;
    }

    const icon = L.icon({
      iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    const marker = L.marker([place.lat, place.lng], { icon }).addTo(this.map);

    marker.bindPopup(`<strong>${place.name}</strong><br>${place.address}`);
    marker.on('click', () => this.selectPlace(place));

    this.placeMarkers[place.id] = marker;
  }

  flyToLocation(place: Place) {
    if (!this.map) {
      return;
    }

    this.map.flyTo([place.lat, place.lng], 16, { duration: 0.7 });
    if (this.placeMarkers[place.id]) {
      this.placeMarkers[place.id].openPopup();
    }
  }

  refreshMarkerHighlights() {
    Object.keys(this.placeMarkers).forEach((id) => {
      const marker = this.placeMarkers[id];
      if (this.selectedPlace && id === this.selectedPlace.id) {
        marker.setZIndexOffset(1000);
      } else {
        marker.setZIndexOffset(0);
      }
    });
  }

  createTaskForSelectedPlace() {
    if (!this.selectedPlace || !this.newTaskNote.trim()) {
      return;
    }

    const task: Task = {
      id: `task-${Date.now()}`,
      placeId: this.selectedPlace.id,
      note: this.newTaskNote.trim(),
      dueDate: this.newTaskDueDate ? this.newTaskDueDate : undefined,
      status: this.newTaskStatus
    };

    this.tasks.push(task);
    this.newTaskNote = '';
    this.newTaskDueDate = '';
    this.newTaskStatus = 'open';
    this.updateTaskMarkers();
  }

  get tasksForSelectedPlace(): Task[] {
    if (!this.selectedPlace) {
      return [];
    }
    return this.tasks.filter(task => task.placeId === this.selectedPlace!.id);
  }

  updateTaskMarkers() {
    if (!this.map || !this.taskMarkerLayer) {
      return;
    }

    this.taskMarkerLayer.clearLayers();

    if (!this.modeShowTasksOnMap) {
      return;
    }

    for (const task of this.tasks) {
      const place = this.findPlaceById(task.placeId);
      if (!place) {
        continue;
      }

      const color = task.status === 'done' ? 'green' : task.status === 'in-progress' ? 'orange' : 'blue';
      const taskMarker = L.circleMarker([place.lat, place.lng], {
        radius: 8,
        color,
        fillOpacity: 0.8
      });

      taskMarker.bindPopup(`<strong>${place.name}</strong><br>${task.note}<br>Status: ${task.status}<br>${task.dueDate ?? ''}`);
      taskMarker.addTo(this.taskMarkerLayer);
    }
  }

  findPlaceById(id: string): Place | undefined {
    return this.places.find(p => p.id === id) || this.searchResults.find(p => p.id === id);
  }

  removeTask(taskId: string) {
    this.tasks = this.tasks.filter(t => t.id !== taskId);
    this.updateTaskMarkers();
  }

  onToggleTaskPins() {
    this.updateTaskMarkers();
  }

  get allPlaces(): Place[] {
    return [...this.searchResults, ...this.places];
  }
}
